import * as yup from 'yup';

// Common field validations
export const email = yup
  .string()
  .email('Please enter a valid email address')
  .required('Email is required');

export const password = yup
  .string()
  .required('Password is required')
  .min(10, 'Password must be at least 10 characters')
  .matches(/[a-z]/, 'Password must contain at least 1 lowercase letter')
  .matches(/[A-Z]/, 'Password must contain at least 1 uppercase letter')
  .matches(/[0-9]/, 'Password must contain at least 1 digit')
  .matches(/[^a-zA-Z0-9]/, 'Password must contain at least 1 special character');

export const firstName = yup
  .string()
  .required('First name is required');

export const lastName = yup
  .string()
  .required('Last name is required');

export const phoneNumber = yup.string();

export const requiredString = (fieldName) => yup
  .string()
  .required(`${fieldName} is required`);

export const optionalString = () => yup.string();

export const requiredNumber = (fieldName) => yup
  .number()
  .typeError(`${fieldName} must be a number`)
  .required(`${fieldName} is required`);

export const optionalNumber = () => yup
  .number()
  .typeError('Must be a number')
  .nullable()
  .transform((value, originalValue) => originalValue === '' ? null : value);

export const requiredPositiveNumber = (fieldName) => yup
  .number()
  .typeError(`${fieldName} must be a number`)
  .positive(`${fieldName} must be positive`)
  .required(`${fieldName} is required`);

export const requiredNonNegativeNumber = (fieldName) => yup
  .number()
  .typeError(`${fieldName} must be a number`)
  .min(0, `${fieldName} must be 0 or more`)
  .required(`${fieldName} is required`);

// Form schemas
export const loginSchema = yup.object({
  email,
  password: yup.string().required('Password is required')
});

export const createAccountSchema = yup.object({
  email,
  password,
  firstName,
  lastName,
  phoneNumber
});

export const forgottenPasswordSchema = yup.object({
  email
});

export const resetPasswordSchema = yup.object({
  email,
  password
});

export const requestInductionSchema = yup.object({
  phoneNumber,
  message: yup.string()
});

// Admin schemas
export const createProductSchema = yup.object({
  credits: requiredNonNegativeNumber('Credits'),
  tokenType: yup.string().oneOf(['general', 'open_gym', 'class']),
  description: optionalString(),
  price: requiredPositiveNumber('Price'),
  enabled: yup.boolean()
});

export const createStaffSchema = yup.object({
  firstName,
  lastName,
  email: yup.string().email('Please enter a valid email address'),
  phoneNumber: optionalString(),
  title: optionalString(),
  bio: optionalString(),
  enabled: yup.boolean()
});

export const createServiceSchema = yup.object({
  name: requiredString('Name'),
  description: optionalString(),
  duration: requiredPositiveNumber('Duration'),
  bufferTime: optionalNumber(),
  price: requiredPositiveNumber('Price'),
  refundWindow: requiredNonNegativeNumber('Cancellation window'),
  depositAmount: requiredNonNegativeNumber('Deposit amount'),
  category: optionalString(),
  isPublished: yup.boolean()
});

export const createCouponSchema = yup.object({
  name: requiredString('Name'),
  code: requiredString('Code'),
  percent_off: requiredPositiveNumber('Discount percentage')
    .max(100, 'Discount cannot exceed 100%')
});

export const createDiscountCodeSchema = yup.object({
  name: requiredString('Name'),
  code: requiredString('Code'),
  discountType: yup.string().oneOf(['percentage', 'amount']).required('Discount type is required'),
  percentageOff: yup.number()
    .transform((value, originalValue) => originalValue === '' ? null : value)
    .nullable()
    .when('discountType', {
      is: 'percentage',
      then: (schema) => schema
        .typeError('Discount percentage must be a number')
        .positive('Discount percentage must be positive')
        .max(100, 'Discount cannot exceed 100%')
        .required('Discount percentage is required'),
      otherwise: (schema) => schema.optional()
    }),
  amountOff: yup.number()
    .transform((value, originalValue) => originalValue === '' ? null : value)
    .nullable()
    .when('discountType', {
      is: 'amount',
      then: (schema) => schema
        .typeError('Discount amount must be a number')
        .positive('Discount amount must be positive')
        .required('Discount amount is required'),
      otherwise: (schema) => schema.optional()
    }),
  start: yup.date().required('Start date is required'),
  end: yup.date().required('End date is required'),
  enabled: yup.boolean()
});

export const updateDiscountCodeSchema = yup.object({
  name: requiredString('Name'),
  start: yup.date().required('Start date is required'),
  end: yup.date().required('End date is required'),
  enabled: yup.boolean()
});

export const createMembershipPlanSchema = yup.object({
  name: requiredString('Name'),
  description: optionalString(),
  amount: requiredPositiveNumber('Amount'),
  interval_count: requiredPositiveNumber('Interval count'),
  interval: requiredString('Interval'),
  active: yup.boolean(),
  maxPayments: yup.number().integer('Max payments must be a whole number').min(1, 'Max payments must be at least 1').nullable().transform((value, original) => original === '' ? null : value),
  subscriptionEndDate: yup.date().nullable().transform((value, original) => original === '' ? null : value),
  tokensPerCycle: yup.number().integer('Tokens per cycle must be a whole number').min(0, 'Tokens per cycle must be 0 or more').nullable().transform((value, original) => original === '' ? null : value),
  openGymTokensPerCycle: yup.number().integer('Open gym tokens per cycle must be a whole number').min(0, 'Open gym tokens per cycle must be 0 or more').nullable().transform((value, original) => original === '' ? null : value),
  classTokensPerCycle: yup.number().integer('Class tokens per cycle must be a whole number').min(0, 'Class tokens per cycle must be 0 or more').nullable().transform((value, original) => original === '' ? null : value),
  tokenExpiryDays: yup.number().integer('Token expiry must be a whole number').min(1, 'Token expiry must be at least 1 day').nullable().transform((value, original) => original === '' ? null : value),
  scheduleStartTime: optionalString(),
  scheduleEndTime: optionalString()
});

export const createStudioSchema = yup.object({
  name: requiredString('Name'),
  lock: optionalString(),
  capacity: requiredPositiveNumber('Capacity').integer('Capacity must be a whole number'),
  enabled: yup.boolean()
});

export const createTransactionSchema = yup.object({
  user: yup.string().required('Please select a user'),
  adjustment: requiredNumber('Adjustment'),
  note: optionalString()
});

// Gift voucher schema
export const giftVoucherCheckoutSchema = yup.object({
  recipientName: requiredString('Recipient name'),
  recipientEmail: email,
  senderName: requiredString('Your name'),
  senderEmail: email,
  message: optionalString()
});
